import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📦 Wiki - Seção AfterPay
 * 
 * Documentação completa sobre o sistema AfterPay (pedidos e logística).
 * Explica como gerenciar pedidos, atualizar status e acompanhar entregas.
 * Voltada para produtores (não técnica).
 */
const AfterPayWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:package-variant-closed" />
          <span>Pedidos AfterPay</span>
        </div>
        <p className={styles.heroText}>
          Gerencie todos os seus pedidos físicos de forma organizada. Acompanhe status, 
          atualize entregas e resolva problemas com facilidade usando o sistema Kanban.
        </p>
      </section>

      {/* O que é AfterPay */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é AfterPay?</span>
          <p className={styles.sectionSubtitle}>
            Sistema de gestão de pedidos físicos
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>AfterPay</strong> é o sistema de gestão de pedidos para produtos físicos. 
              Ele permite que você <strong>acompanhe e gerencie</strong> todos os pedidos que 
              precisam ser enviados fisicamente, desde a confirmação até a entrega.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              O sistema usa um <strong>Kanban visual</strong> onde cada pedido é representado 
              por um card que pode ser movido entre diferentes colunas conforme o status muda.
            </p>
          </div>
        </div>
      </section>

      {/* Como Acessar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar?</span>
          <p className={styles.sectionSubtitle}>
            Encontre no menu lateral
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Vendas → Pedidos de AfterPay</strong>.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Sistema Kanban */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Sistema Kanban</span>
          <p className={styles.sectionSubtitle}>
            Organize pedidos visualmente em colunas
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Pendente</div>
                <span className={styles.cardTitle}>Pedidos Pendentes</span>
                <p>
                  Pedidos que foram confirmados mas ainda <strong>não foram processados</strong>. 
                  Aqui você prepara os produtos para envio.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:package-variant" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Processando</div>
                <span className={styles.cardTitle}>Em Processamento</span>
                <p>
                  Pedidos que estão sendo <strong>preparados para envio</strong>. Produtos sendo 
                  embalados e etiquetas sendo geradas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:truck-delivery" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Enviado</div>
                <span className={styles.cardTitle}>Em Trânsito</span>
                <p>
                  Pedidos que foram <strong>enviados e estão a caminho</strong> do cliente. 
                  Aqui você acompanha o código de rastreamento.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Entregue</div>
                <span className={styles.cardTitle}>Entregue</span>
                <p>
                  Pedidos que foram <strong>entregues com sucesso</strong> ao cliente. 
                  O ciclo está completo!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Usar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Usar o Sistema?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para gerenciar pedidos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Visualize os Pedidos
                </span>
                <p>
                  Os pedidos aparecem como <strong>cards no Kanban</strong>. Cada card mostra 
                  informações importantes: cliente, produto, valor e status.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cursor-move" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Mova os Cards
                </span>
                <p>
                  <strong>Arraste e solte</strong> os cards entre as colunas para atualizar o 
                  status do pedido. Isso atualiza automaticamente no sistema!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:information-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Veja Detalhes
                </span>
                <p>
                  Clique em um card para ver <strong>detalhes completos</strong> do pedido: 
                  dados do cliente, endereço de entrega, código de rastreamento, etc.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:update" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Atualize Informações
                </span>
                <p>
                  Nos detalhes do pedido, você pode <strong>adicionar código de rastreamento</strong>, 
                  atualizar status manualmente e adicionar observações.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Funcionalidades */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Funcionalidades Principais</span>
          <p className={styles.sectionSubtitle}>
            O que você pode fazer no AfterPay
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:feature-search" className={styles.infoIcon} />
          <div>
            <p>
              O sistema AfterPay oferece várias funcionalidades para facilitar o gerenciamento:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Busca de pedidos:</strong> Encontre pedidos específicos por cliente, produto ou código</li>
              <li><strong>Filtros por data:</strong> Veja pedidos de períodos específicos (últimos 7, 30, 90 dias)</li>
              <li><strong>Estatísticas:</strong> Acompanhe totais, receitas e tendências de vendas</li>
              <li><strong>Tags personalizadas:</strong> Organize pedidos com tags coloridas</li>
              <li><strong>Histórico completo:</strong> Veja todas as mudanças de status de cada pedido</li>
              <li><strong>Exportação:</strong> Exporte dados para relatórios externos</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Como usar o AfterPay de forma eficiente
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:update" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Atualize Status Regularmente
                </span>
                <p>
                  Mantenha os status atualizados para que clientes e você tenham visibilidade 
                  real do andamento dos pedidos.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:tag-multiple" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Use Tags para Organizar
                </span>
                <p>
                  Crie tags personalizadas para <strong>categorizar pedidos</strong>: urgente, 
                  presente, fragil, etc. Isso facilita a organização!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:truck-fast" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Adicione Código de Rastreamento
                </span>
                <p>
                  Sempre que enviar um pedido, <strong>adicione o código de rastreamento</strong>. 
                  Isso permite que o cliente acompanhe a entrega.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:chart-line" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acompanhe Estatísticas
                </span>
                <p>
                  Use as <strong>estatísticas e gráficos</strong> para entender tendências, 
                  identificar picos de vendas e planejar melhor seu estoque.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default AfterPayWiki;

